import constants from './constants.js';
import { new_lin_table, withAssignedLin } from './tables/lin_adapter.js';
import { notifySelectionChanged } from './tables/lin_selection.js';
import { getPageConfig } from './pageConfig.js';
import { pieChart } from './pieChart.js';
import { readPayload } from './payload.js';
import { sectionLoaded } from './rxiv_utils.js';
import { create_tree, handlePhylogenyCreation, pollJobStatus } from './tree.js';
import { IDENTITY_PREFIX, attrsOf, pruneLeaves, replaceLeafNames, structureTreeLeafMapping } from './tree_modified.js';

import { displayNoDataOverlay, getChartContainerDimensions } from './charts_utils.js';
import { getPhylotreeJobId } from './modules/activities.module.js';
import { JOB_STATUS, isJobFailed, isJobSuccess, isJobTerminal } from './modules/job_status.module.js';
import { timeSeriesPlot } from './timeSeries.js';
import { createBubbleMap, createHeatMap, loadGeoData } from './worldMap.js';

function getD3() {
  return globalThis.d3;
}

const { GENOME_FIELDS, IDENTIFY_FIELDS, IDENTIFY_RENAME } = constants;

// Constants and configuration
export const CONFIG = {
  POLLING_INTERVAL: 5000,
  MAX_POLLING_INTERVAL: 60000,
  // hard max limit on how many elements can be chosen to run a phylogeny process
  // anything above this is now allowed
  SOFT_PHYLOTREE_THRESHOLD: 200,
  HARD_PHYLOTREE_THRESHOLD: 1000,
  SVG_NAMESPACE: "http://www.w3.org/2000/svg",
  BASE_URL: "/api/jobs",
  STORAGE_KEY: "Analysis",
  // 17 MB of country outlines. Fetched by ensureGeoData() the first time a map tab needs
  // it, never on page load.
  GEO_DATA_URL: "/static/custom.geo_50m.json"
};

// Cache DOM queries
const DOM = {
  graphContainer: () => document.getElementById("graphContainer"),
  errorAlert: () => document.getElementById("error-alert"),
  loadingOverlay: () => document.getElementById("loading-overlay")
};

// retrieve config from data attribute on app-config element
const config = getPageConfig();
export default config;

// State management
export class AppStateManager {
  constructor() {
    this.state = {
      isDataLoaded: false,
      analysis: {},
      linGroups: {},
      genomes: {},
      identities: { genome: {}, signature: {} },
      all_genomes: {},
      geoData: null,
      phyloJobId: null,
      phylotreeData: null,
      jobResults: {}
    };
  }

  loadFromStorage() {
    const stored = JSON.parse(localStorage.getItem(CONFIG.STORAGE_KEY)) || {};
    this.state.analysis = stored;
    this.state.linGroups = stored.selectedLinGroups || {};
    this.state.genomes = stored.selectedGenomes || {};
    this.state.identities = stored.selectedIdentities || {
      genome: {},
      signature: {}
    };

    // Clean up state
    if (this.state.linGroups[0]) {
      delete this.state.linGroups[0];
    }

    this.state.all_genomes = { ...this.state.genomes };
  }

  saveToStorage() {
    localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(this.state.analysis));
  }
}

// Event handling with debounce
const EventHandler = {
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  setupEventListeners() {
    // Storage events
    window.addEventListener("storage", (event) => {
      if (event.key === CONFIG.STORAGE_KEY) {
        const alertElement = DOM.errorAlert();
        if (alertElement) {
          alertElement.style.display = "block";
        }
      }
    });

    // Genome removal events
    document.addEventListener("genomeRemoved", this.handleGenomeRemoval);

    // Section clear events
    document.querySelectorAll(".clear-section-btn")
      .forEach(btn => btn.addEventListener("click", this.handleSectionClear));
  }
};

/** The in-flight GeoJSON fetch, so concurrent map tabs share one download. */
let geoDataPromise = null;

/**
 * Resolves the world map GeoJSON, fetching it on first use only.
 *
 * initAppState() used to download this on every result and analysis page load and await
 * it before rendering anything, 17 MB raw, for two tabs most visits never open. The
 * heatmap and bubble map call this instead when they actually have something to draw.
 *
 * @returns {Promise<Object>} the parsed GeoJSON, cached in AppState after the first fetch.
 */
export function ensureGeoData() {
  if (AppState.state.geoData) {
    return Promise.resolve(AppState.state.geoData);
  }
  if (!geoDataPromise) {
    geoDataPromise = loadGeoData(CONFIG.GEO_DATA_URL)
      .then((data) => {
        AppState.state.geoData = data;
        return data;
      })
      .catch((error) => {
        // Drop the failed attempt so the next map tab retries instead of inheriting it.
        geoDataPromise = null;
        throw error;
      });
  }
  return geoDataPromise;
}

// API Service
class APIService {
  static async pollJobStatus(jobId, containerId = null, interval = CONFIG.POLLING_INTERVAL) {
    if (!jobId) return Promise.reject("Invalid job ID");

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const response = await fetch(`${CONFIG.BASE_URL}/${jobId}`);
          const data = await response.json();

          if (isJobSuccess(data.status)) {
            resolve(data.result);
          } else if (isJobFailed(data.status)) {
            reject(data.error);
          } else {
            setTimeout(poll, interval);
          }
        } catch (error) {
          setTimeout(poll, interval);
        }
      };

      poll();
    });
  }
}

// UI Helper
const UIHelper = {
  showError: EventHandler.debounce((message) => {
    const alert = DOM.errorAlert() || document.createElement("div");
    alert.className = "alert alert-danger alert-dismissible fade show";
    alert.innerHTML = `<strong>Error:</strong> ${message}
            <button type="button" class="btn-close" data-bs-dismiss="alert"></button>`;
    document.body.appendChild(alert);
    setTimeout(() => alert.remove(), 5000);
  }, 300),

  displayLoadingMessage(containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;

    container.innerHTML = `
            <div class="loading-overlay">
                <div class="spinner-border text-primary"></div>
                <div class="mt-2">Loading...</div>
            </div>`;
  }
};

export var AppState = new AppStateManager();



/**
 * Initialize the application
 *
 * @param {Object} [options]
 * @param {boolean} [options.resolveLinGroups=true] resolve the analysis cart's stored
 *   LINgroups into member lists (a prefix_genome_members job per unresolved group, then
 *   polling). Only the analysis page shows the cart; the result page passes false so that
 *   opening a result never queues jobs for it.
 */
export async function initAppState({ resolveLinGroups = true } = {}) {
  try {

    // Initialize app state
    if (!AppState) {
      AppState = new AppStateManager();
    }

    AppState.loadFromStorage();

    // Always yield here, even with nothing to resolve. The result page takes the spinner
    // synchronously right after calling this (getJob in result.js), and renderAllUI()
    // ends in sectionLoaded(), which hides the spinner unless someone holds it.
    await (resolveLinGroups ? fetchAllLinGroupData() : Promise.resolve());

    // Global state object to store all application data
    AppState.state = {
      ...AppState.state,
      uuid: config.uuid || "",
      baseUrl: config.jobUrl,
      genomeApiUrl: config.genomeApiUrl,
    };

    AppState.state.isDataLoaded = true;
    renderAllUI();
    EventHandler.setupEventListeners();
  } catch (error) {
    console.error("Initialization error:", error);
    UIHelper.showError("Failed to initialize application. Please try refreshing the page.");
    // Without this a failed init left the spinner covering the error message.
    sectionLoaded();
  }
}

/** 
 * Start a job for a LINgroup to fetch its genome members
 * @param {number} groupId - The ID of the LINgroup.
 * @param {Object} group - The LINgroup object containing its data.
 * @returns {Promise} - Resolves with the job UUID when the job is started.
*/
export function startLinGroupJob(groupId, group) {
  const linSequence =
    group.slin || (Array.isArray(group.lin) ? group.lin.join(",") : "");

  return new Promise((resolve, reject) => {
    $.ajax({
      url: "/api/jobs",
      method: "POST",
      data: {
        name: "prefix_genome_members",
        title: "Genomes in LINgroup: " + linSequence,
        args: {
          slins: linSequence,
        },
      },
      success: function (res) {
        if (res.status === 0) {
          AppState.state.jobResults[groupId] = {
            jobId: res.uuid,
            linSequence: linSequence,
            status: "started",
            result: null,
          };
          resolve(res.uuid);
        } else {
          reject(
            new Error(
              `Failed to start job for LINgroup ${groupId}: ${res.message}`
            )
          );
        }
      },
      error: function (xhr, errorMsg, error) {
        reject(
          new Error(`Error starting job for LINgroup ${groupId}: ${errorMsg}`)
        );
      },
    });
  });
}

/**
 * Ensures all LINgroups have a list of genome IDs, fetching from the server if necessary,
 * and saves these ID lists to localStorage.
 * @returns {Promise} - Resolves when all LINgroup data is fetched and stored.
 */
export async function fetchAllLinGroupData() {
  if (Object.keys(AppState.state.linGroups).length === 0) {
    return Promise.resolve();
  }

  const originalLinGroups = {
    ...AppState.state.linGroups,
  };
  const originalCount = Object.keys(originalLinGroups).length;

  // Filter the child prefixes using your existing function.
  const filteredLinGroups = filterChildPrefixesByLin(originalLinGroups);
  const filteredCount = Object.keys(filteredLinGroups).length;

  // Update the state for the current session.
  AppState.state.linGroups = filteredLinGroups;
  console.log("Filtered LINgroups:", AppState.state.linGroups);

  // If the filtering removed any prefixes, update localStorage to make it permanent.
  if (filteredCount < originalCount) {
    const removedCount = originalCount - filteredCount;
    console.log(
      `Persistently removing ${removedCount} child LINgroup(s) from storage.`
    );

    // Get the current analysis data from localStorage.
    let analysisData = JSON.parse(localStorage.getItem("Analysis")) || {};

    // Replace the old list of lingroups with the new, filtered list.
    analysisData.selectedLinGroups = filteredLinGroups;

    // Save the updated analysis object back to localStorage.
    localStorage.setItem("Analysis", JSON.stringify(analysisData));

    // Also, ensure the main AppState.state.analysis object stays in sync.
    AppState.state.analysis = analysisData;
  }

  const groupsNeedingJobs = Object.entries(AppState.state.linGroups).filter(
    ([groupId, group]) => !Array.isArray(group.genomes)
  );

  if (groupsNeedingJobs.length > 0) {
    console.log(
      `Starting jobs for ${groupsNeedingJobs.length} LINgroups to fetch genome IDs.`
    );
    const jobStartPromises = groupsNeedingJobs.map(([groupId, group]) =>
      startLinGroupJob(groupId, group)
    );
    await Promise.all(jobStartPromises);
    const jobResults = await pollAllJobs();

    let analysisData = JSON.parse(localStorage.getItem("Analysis")) || {};
    analysisData.selectedLinGroups = analysisData.selectedLinGroups || {};
    let wasLocalStorageUpdated = false;

    Object.entries(jobResults).forEach(([groupId, jobInfo]) => {
      if (
        isJobSuccess(jobInfo.status) &&
        jobInfo.result &&
        Array.isArray(jobInfo.result[0]?.data)
      ) {
        const genomeIds = jobInfo.result[0].data;

        if (AppState.state.linGroups[groupId]) {
          AppState.state.linGroups[groupId].genomes = genomeIds;
        }

        if (analysisData.selectedLinGroups[groupId]) {
          analysisData.selectedLinGroups[groupId].genomes = genomeIds;
          wasLocalStorageUpdated = true;
        }
      } else if (isJobFailed(jobInfo.status)) {
        console.error(`Job for LINgroup ${groupId} failed:`, jobInfo.error);
      }
    });

    if (wasLocalStorageUpdated) {
      console.log("Saving newly fetched genome ID lists to localStorage.");
      localStorage.setItem("Analysis", JSON.stringify(analysisData));
    }
  }

  return fetchAndPopulateLiveGenomeData();
}

/**
 * Fetches full genome details "on the fly" and populates a temporary, non-persistent cache.
 * This function does NOT save the large genome objects to localStorage.
 * @returns {Promise} - Resolves when all genome data is fetched and stored in session cache.
 */
export async function fetchAndPopulateLiveGenomeData() {
  const allGenomeIds = new Set();

  // Collect all genome IDs from every LINgroup.
  Object.values(AppState.state.linGroups).forEach((group) => {
    if (Array.isArray(group.genomes)) {
      group.genomes.forEach((id) => allGenomeIds.add(id));
    }
  });

  // Also include any individually selected genomes.
  Object.keys(AppState.state.genomes).forEach((id) => allGenomeIds.add(id));

  const idArray = Array.from(allGenomeIds);
  if (idArray.length === 0) {
    return Promise.resolve();
  }

  console.log(`Fetching live details for ${idArray.length} unique genomes.`);

  try {
    const res = await $.ajax({
      url: "/api/genomes",
      method: "GET",
      type: "json",
      data: {
        ids: JSON.stringify(idArray),
      },
    });

    // Store all fetched data in the NON-PERSISTENT `AppState.state.all_genomes` cache for this session.
    const genomeData = res.data || {};

    // Append new genome data to existing all_genomes
    Object.assign(AppState.state.all_genomes, genomeData);
    // Parse every Attributes_JSON once, now: each chart processor and tree walk reads
    // the memoized object instead of re-parsing the same string per genome per render.
    Object.values(genomeData).forEach(attrsOf);
  } catch (error) {
    console.error("Failed to fetch live genome details:", error);
    showError(
      "An error occurred while fetching live genome data. The page might not display correctly."
    );
    throw error;
  }
}

/** 
  * Poll all jobs until completion with timeout
  * @param {number} timeout - Maximum time to wait for all jobs (in milliseconds).
  * @returns {Promise} - Resolves with job results when all jobs complete.
  */
export function pollAllJobs(timeout = 120000) {
  const startTime = Date.now();
  const jobIds = Object.keys(AppState.state.jobResults);
  if (jobIds.length === 0) {
    return Promise.resolve({});
  }
  return new Promise((resolve, reject) => {
    const checkAllJobs = async () => {
      // Check for timeout
      if (Date.now() - startTime > timeout) {
        reject(
          new Error(`Job polling timed out after ${timeout / 1000} seconds`)
        );
        return;
      }
      // Check each job's status
      const pendingJobs = jobIds.filter(
        (id) => !isJobTerminal(AppState.state.jobResults[id].status)
      );
      if (pendingJobs.length === 0) {
        resolve(AppState.state.jobResults);
        return;
      }
      // Check each pending job
      for (const jobId of pendingJobs) {
        try {
          await checkJobStatus(jobId);
        } catch (error) {
          console.error(`Error checking job ${jobId}:`, error);
          AppState.state.jobResults[jobId].status = JOB_STATUS.ERROR;
          AppState.state.jobResults[jobId].error = error.message;
        }
      }
      // Check again after delay
      setTimeout(checkAllJobs, 2000);
    };
    // Start the checking process
    checkAllJobs();
  });
}

/**
 * Check the status of a single job
 * @param {string} jobId - The UUID of the job to check.
 * @returns {Promise} - Resolves with job result if completed, otherwise null.
 */
export function checkJobStatus(jobId) {
  const jobInfo = AppState.state.jobResults[jobId];
  return new Promise((resolve, reject) => {
    $.ajax({
      url: `/api/jobs/${jobInfo.jobId}`,
      method: "GET",
      dataType: "json",
      success: function (jobRes) {
        if (isJobSuccess(jobRes.data.status)) {
          // The sections list, whichever shape the result was stored in (payload.js);
          // a lingroup_members result is one genome-table section.
          let sections;
          try {
            sections = readPayload(jobRes.data.result).sections;
          } catch (err) {
            AppState.state.jobResults[jobId].status = JOB_STATUS.ERROR;
            AppState.state.jobResults[jobId].error = err.message;
            reject(err);
            return;
          }
          AppState.state.jobResults[jobId].status = JOB_STATUS.SUCCESS;
          AppState.state.jobResults[jobId].result = sections;
          resolve(jobRes);
        } else if (isJobFailed(jobRes.data.status)) {
          AppState.state.jobResults[jobId].status = JOB_STATUS.ERROR;
          AppState.state.jobResults[jobId].error =
            jobRes.data.result || jobRes.data.message || "Job failed";
          reject(new Error(`Job ${jobId} failed: ${jobRes.data.message}`));
        } else {
          // Job still running
          resolve(null);
        }
      },
      error: function (xhr, errorMsg, error) {
        reject(new Error(`Error checking job ${jobId}: ${errorMsg}`));
      },
    });
  });
}

/**
 * Efficiently filters out child prefixes based on their 'lin' (lineage) array
 * using a Trie (prefix tree) data structure. A prefix is considered a child
 * if its lineage array is a descendant of another prefix's lineage array.
 *
 * @param {Object} prefixDict - Dictionary of prefix IDs and their data.
 * @returns {Object} A new dictionary containing only the prefixes that are not children of any other prefix.
 */
export function filterChildPrefixesByLin(prefixDict) {
  // Defines a node in the Trie.
  class TrieNode {
    constructor() {
      this.children = new Map();
      this.prefixId = null; // Store the ID if a lineage ends at this node
    }
  }

  const root = new TrieNode();
  const allPrefixIds = Object.keys(prefixDict);

  // Build the Trie by inserting all lineage arrays.
  for (const prefixId of allPrefixIds) {
    const lin = prefixDict[prefixId].lin;
    if (!lin || !Array.isArray(lin)) continue; // Skip if lin is invalid

    let currentNode = root;
    for (const num of lin) {
      if (!currentNode.children.has(num)) {
        currentNode.children.set(num, new TrieNode());
      }
      currentNode = currentNode.children.get(num);
    }
    // Mark the end of the lineage path with its corresponding prefix ID.
    currentNode.prefixId = prefixId;
  }

  // Identify all prefixes that are children of another.
  const prefixesToRemove = new Set();
  for (const prefixId of allPrefixIds) {
    const lin = prefixDict[prefixId].lin;
    if (!lin || !Array.isArray(lin)) continue;

    let currentNode = root;
    // Traverse the Trie for the current lineage.
    // Check every node on the path (before the end) to see if it's a terminal node.
    for (let i = 0; i < lin.length; i++) {
      const num = lin[i];
      currentNode = currentNode.children.get(num);

      // If a node on the path is a terminal node for another prefix,
      // then the current prefix is a child of that other prefix.
      if (currentNode.prefixId !== null && currentNode.prefixId !== prefixId) {
        prefixesToRemove.add(prefixId);
        break; // Found a parent, no need to check further for this prefix.
      }
    }
  }

  // Create the new, filtered dictionary.
  const filteredDict = {};
  for (const prefixId of allPrefixIds) {
    if (!prefixesToRemove.has(prefixId)) {
      filteredDict[prefixId] = prefixDict[prefixId];
    }
  }

  return filteredDict;
}

/**
 * Render all UI components once data is loaded
 */
export function renderAllUI() {
  if (!AppState.state.isDataLoaded) {
    console.warn("Attempted to render UI before data was loaded");
    return;
  }
  // Show the result-body div
  document.getElementById("genome-search-tab-content").classList.remove("is-hidden");
  // loadingSections += 1;
  // Render the three main sections
  renderIdentityTable();
  renderLinGroupsTable();
  renderGenomesTable();

  // Clear the spinner here rather than relying on new_lin_table() to do it: each renderer
  // above returns early when its section has no data, so with nothing selected no table
  // was ever built and the spinner ran forever.
  sectionLoaded();
}

/**
 * Render the identity table with nested accordions for types
 * (e.g., genomes, signatures)
 */
export function renderIdentityTable() {
  const tableBody = document.getElementById("identityTableBody");
  if (!tableBody) return;
  tableBody.innerHTML = ""; // Clear previous content

  const identityTypes = {
    genome: "Uploaded Genomes",
    signature: "Uploaded Signatures",
  };
  let totalIdentities = 0;
  const hasGenomes =
    AppState.state.identities.genome &&
    Object.keys(AppState.state.identities.genome).length > 0;
  const hasSignatures =
    AppState.state.identities.signature &&
    Object.keys(AppState.state.identities.signature).length > 0;
  if (!hasGenomes && !hasSignatures) {
    tableBody.innerHTML =
      '<div class="alert alert-info m-2">No identity data selected</div>';
    const mainHeader = document
      .getElementById("headingIdentity")
      .querySelector("button.accordion-button");
    addCountBadge(mainHeader, 0);
    return;
  }
  const nestedAccordion = document.createElement("div");
  nestedAccordion.className = "accordion";
  nestedAccordion.id = "identity-nested-accordion";
  // Loop through the types and create an accordion for each
  for (const type in identityTypes) {
    const data = AppState.state.identities[type] || {};
    const count = Object.keys(data).length;
    totalIdentities += count;
    if (count > 0) {
      const typeName = identityTypes[type];
      const accordionItem = document.createElement("div");
      accordionItem.className = "accordion-item";
      const header = document.createElement("h2");
      header.className = "accordion-header";
      header.id = `heading-identity-${type}`;
      const button = document.createElement("button");
      button.className = "accordion-button collapsed p-2";
      button.type = "button";
      button.dataset.bsToggle = "collapse";
      button.dataset.bsTarget = `#collapse-identity-${type}`;
      button.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-controls", `collapse-identity-${type}`);
      button.textContent = typeName;
      addCountBadge(button, count);
      const collapseDiv = document.createElement("div");
      collapseDiv.id = `collapse-identity-${type}`;
      collapseDiv.className = "accordion-collapse collapse";
      collapseDiv.setAttribute("aria-labelledby", `heading-identity-${type}`);
      const accordionBody = document.createElement("div");
      accordionBody.className = "accordion-body p-0 pb-1";
      // Prepare data for new_lin_table
      const sectionData = {
        data: data,
        dom: $($("#genome-table-template").get(0).content.cloneNode(true))
          .find(".card")
          .attr("id", `section-identity-${type}`),
        // The page-local view selection: everything checked by default, unchecking
        // filters the visualizations, and localStorage is never touched.
        selection: viewSelection,
      };
      accordionBody.appendChild(sectionData.dom[0]);
      collapseDiv.appendChild(accordionBody);
      header.appendChild(button);
      accordionItem.appendChild(header);
      accordionItem.appendChild(collapseDiv);
      nestedAccordion.appendChild(accordionItem);

      // Call new_lin_table to populate the table
      new_lin_table(
        sectionData,
        IDENTIFY_FIELDS,
        typeName,
        `${count} ${count === 1 ? 'item' : 'items'}`,
        undefined,
        undefined,
        IDENTIFY_RENAME,
        false
      );
    }
  }
  tableBody.appendChild(nestedAccordion);
}

/**
 * Render the LINgroups table
 * 
 */
export function renderLinGroupsTable() {
  const tableBody = document.getElementById("linTableBody");
  if (!tableBody) return;
  tableBody.innerHTML = "";
  if (!AppState.state.linGroups || Object.keys(AppState.state.linGroups).length === 0) {
    tableBody.innerHTML =
      '<div class="alert alert-info">No LINgroups selected</div>';
    return;
  }
  const accordionContainer = document.createElement("div");
  accordionContainer.className = "accordion";
  accordionContainer.id = "linAccordion";

  Object.entries(AppState.state.linGroups).forEach(([groupId, group]) => {
    const accordionItem = createLinGroupAccordionItem(group, groupId);
    accordionContainer.appendChild(accordionItem);
  });

  tableBody.appendChild(accordionContainer);

  // Now populate each LINgroup's content
  Object.entries(AppState.state.linGroups).forEach(([groupId, group]) => {
    if (group.genomes) {
      populateLinGroupContent(groupId, group);
    }
  });
}

/**
 * Create an accordion item for a LINgroup
 * @param {Object} group - The LINgroup object containing its data.
 * @param {number} groupId - The ID of the LINgroup.
 * @return {HTMLElement} - The accordion item element.
 */
export function createLinGroupAccordionItem(group, groupId) {
  const linSequence = group.slin
    ? group.slin
    : Array.isArray(group.lin)
      ? group.lin.join(",")
      : "";

  const accordionItem = document.createElement("div");
  accordionItem.className = "accordion-item";

  const header = document.createElement("h1");
  header.className = "accordion-header d-flex align-items-center";
  header.id = "linHeading" + groupId;

  const button = document.createElement("button");
  button.className = "accordion-button collapsed p-2 flex-grow-1";
  button.type = "button";
  button.setAttribute("data-bs-toggle", "collapse");
  button.setAttribute("data-bs-target", "#linCollapse" + groupId);
  button.setAttribute("aria-expanded", "false");
  button.setAttribute("aria-controls", "linCollapse" + groupId);
  button.textContent = "LINgroup: " + linSequence;

  const deleteButton = document.createElement("button");
  deleteButton.className = "btn btn-sm btn-outline-danger clear-lingroup-btn";
  deleteButton.setAttribute("data-groupid", groupId);
  deleteButton.setAttribute("title", "Remove this LINgroup");

  const deleteIcon = document.createElement("i");
  deleteIcon.className = "fa fa-trash fa-lg";
  deleteIcon.setAttribute("aria-hidden", "true");
  deleteButton.appendChild(deleteIcon);

  header.appendChild(button);
  header.appendChild(deleteButton);
  accordionItem.appendChild(header);

  const collapseDiv = document.createElement("div");
  collapseDiv.className = "accordion-collapse collapse";
  collapseDiv.id = "linCollapse" + groupId;
  collapseDiv.setAttribute("aria-labelledby", "linHeading" + groupId);

  const bodyDiv = document.createElement("div");
  bodyDiv.className = "accordion-body p-0 pb-1";
  bodyDiv.id = "linBody" + groupId;

  collapseDiv.appendChild(bodyDiv);
  accordionItem.appendChild(collapseDiv);

  return accordionItem;
}

/**
 * Populates a LINgroup's accordion content with its genome data.
 * It reads the array of genome IDs from the group object and uses them
 * to look up the full genome details from the AppState.state.all_genomes cache.
 * @param {number} groupId - The ID of the LINgroup.
 * @param {Object} group - The LINgroup object containing its data.
 */
export function populateLinGroupContent(groupId, group) {
  const bodyDiv = document.getElementById("linBody" + groupId);
  if (!bodyDiv) {
    console.error(`Could not find element linBody${groupId}`);
    return;
  }

  // 'group.genomes' is now expected to be an array of genome IDs.
  const genomeIds = group.genomes;
  if (!Array.isArray(genomeIds) || genomeIds.length === 0) {
    bodyDiv.innerHTML =
      '<div class="alert alert-info m-2">No genomes found for this LINgroup.</div>';
    addCountBadge(
      document
        .getElementById(`linHeading${groupId}`)
        .querySelector("button.accordion-button"),
      0
    );
    return;
  }

  // Build the data object for the table by looking up details in the global `all_genomes` cache.
  const genomesForTable = {};
  genomeIds.forEach((id) => {
    if (AppState.state.all_genomes[id]) {
      genomesForTable[id] = AppState.state.all_genomes[id];
    } else {
      console.warn(
        `Genome data for ID ${id} not found in AppState.state.all_genomes`
      );
    }
  });

  const genomeSection = {
    data: genomesForTable, // Pass the constructed object of full genome data.
    dom: $($("#genome-table-template").get(0).content.cloneNode(true))
      .find(".card")
      .attr("id", "section-lin-" + groupId),
    title: `Genomes in LINgroup: ${group.slin || (Array.isArray(group.lin) ? group.lin.join(",") : "")
      }`,
    selection: viewSelection,
  };
  bodyDiv.appendChild(genomeSection.dom[0]);

  new_lin_table(
    genomeSection,
    GENOME_FIELDS,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    false
  );

  // Update the count badge for this LINgroup.
  const header = document
    .getElementById(`linHeading${groupId}`)
    .querySelector("button.accordion-button");
  addCountBadge(header, Object.keys(genomesForTable).length);
}

/**
 * Update just a single LINgroup's content
 * @param {number} groupId - The ID of the LINgroup to update.
 * @return {boolean} - True if update was successful, false otherwise.
 */
export function updateSingleLinGroup(groupId) {
  // Check if the LINgroup exists
  if (!AppState.state.linGroups[groupId]) {
    console.error(`LINgroup ${groupId} not found`);
    return false;
  }

  const group = AppState.state.linGroups[groupId];

  // Find the body div for this LINgroup
  const bodyDiv = document.getElementById(`linBody${groupId}`);
  if (!bodyDiv) {
    console.error(`Could not find element linBody${groupId}`);
    return false;
  }

  // Clear and repopulate just this group's content
  bodyDiv.innerHTML = "";

  // Re-use the existing populateLinGroupContent function
  populateLinGroupContent(groupId, group);

  return true;
}

/** Render the genomes table
 */
export function renderGenomesTable() {
  const tableBody = document.getElementById("genomesTableBody");
  if (!tableBody) return;
  tableBody.innerHTML = "";

  if (!AppState.state.genomes || Object.keys(AppState.state.genomes).length === 0) {
    tableBody.innerHTML =
      '<div class="alert alert-info m-2">No genomes selected</div>';
    addCountBadge(
      document
        .getElementById("headingGenomes")
        .querySelector("button.accordion-button"),
      0
    );
    return;
  }

  // Create a Set of all genome IDs that are part of any selected LINgroup for efficient lookup.
  const idsInLinGroups = new Set();
  for (const groupId in AppState.state.linGroups) {
    const group = AppState.state.linGroups[groupId];
    if (group && Array.isArray(group.genomes)) {
      group.genomes.forEach((id) => idsInLinGroups.add(String(id)));
    }
  }

  // Create a new list of genomes that are NOT in a LINgroup.
  const filteredGenomes = {};
  const originalCount = Object.keys(AppState.state.genomes).length;

  Object.keys(AppState.state.genomes).forEach((genomeID) => {
    if (!idsInLinGroups.has(String(genomeID))) {
      filteredGenomes[genomeID] = AppState.state.genomes[genomeID];
    }
  });

  // If the number of genomes in the filtered list is less than the original,
  // it means some were absorbed by LINgroups. We now update the persistent state.
  if (Object.keys(filteredGenomes).length < originalCount) {
    console.log(
      "Removing genomes from 'selectedGenomes' as they are now part of a selected LINgroup."
    );

    // Update the state for the current session.
    AppState.state.genomes = filteredGenomes;

    // Update localStorage to reflect this change.
    AppState.state.analysis.selectedGenomes = filteredGenomes;
    localStorage.setItem("Analysis", JSON.stringify(AppState.state.analysis));
  }

  const count = Object.keys(filteredGenomes).length;
  const header = document
    .getElementById("headingGenomes")
    .querySelector("button.accordion-button");
  addCountBadge(header, count);

  if (count === 0) {
    tableBody.innerHTML =
      '<div class="alert alert-info m-2">No individually selected genomes to display.<br><small>Genomes that are part of a selected LINgroup are shown under that group.</small></div>';
    return;
  }

  const genomeSection = {
    data: filteredGenomes, // Use the final, filtered data for the table.
    dom: $($("#genome-table-template").get(0).content.cloneNode(true))
      .find(".card")
      .attr("id", "section-genome"),
    title: "Selected Genomes",
    subtitle: "These are your selected Genomes",
    selection: viewSelection,
  };

  tableBody.appendChild(genomeSection.dom[0]);

  new_lin_table(
    genomeSection,
    GENOME_FIELDS,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    false
  );
}

/**
 * Which rows feed the visualizations: the analysis page's own, in-memory selection.
 *
 * Every row starts checked, and unchecking one excludes it from the charts and the
 * tree without touching the localStorage `Analysis` document. That distinction is the
 * point: the store's SelectionSet routes an uncheck through removeGenomeFromLocal,
 * which permanently deletes the genome from the basket (and can splice it out of a
 * LINgroup's genome list), destructive "remove from basket" where the page needs
 * "leave out of the charts". It also made LINgroup-accordion rows render unchecked,
 * because membership through a group is not `genomeSelectedExactly`.
 *
 * Implements exactly the surface LinGrid consumes from SelectionSet (`has`,
 * `coveredBy`, `set`, `setMany`). Exclusions are per page load; a reload starts
 * all-checked again.
 */
class AnalysisViewSelection {
  constructor() {
    this.excluded = new Set();
    this._timer = null;
  }

  has(model) {
    return !this.excluded.has(String(model._id));
  }

  /** Nothing is ever "covered" here: view membership is per row, not per prefix. */
  coveredBy() {
    return null;
  }

  set(model, checked) {
    const id = String(model._id);
    const flipped = checked ? this.excluded.delete(id) : !this.excluded.has(id);
    if (!checked) this.excluded.add(id);
    if (flipped) this._changed();
    return { changed: flipped ? 1 : 0, stored: flipped ? 1 : 0, skipped: 0, ok: true };
  }

  setMany(models, checked) {
    let changed = 0;
    models.forEach((m) => {
      const id = String(m._id);
      if (checked) {
        if (this.excluded.delete(id)) changed++;
      } else if (!this.excluded.has(id)) {
        this.excluded.add(id);
        changed++;
      }
    });
    if (changed) this._changed();
    return { changed, stored: changed, skipped: 0, ok: true };
  }

  /** Raw tree leaf names are genomeIDs or "identity-<key>"; both map to row ids. */
  isExcludedRawLeaf(rawName) {
    if (rawName == null || !this.excluded.size) return false;
    const name = String(rawName);
    if (name.startsWith("identity-")) {
      return this.excluded.has(name.slice("identity-".length));
    }
    return this.excluded.has(name);
  }

  /** Shallow copy of an id-keyed dictionary with the excluded rows dropped. */
  filterObject(obj) {
    if (!this.excluded.size || !obj) return obj;
    const out = {};
    Object.keys(obj).forEach((k) => {
      if (!this.excluded.has(String(k))) out[k] = obj[k];
    });
    return out;
  }

  _changed() {
    // Repaint every grid's checkboxes (they all subscribe through onSelectionChange),
    // then re-render the visible chart once the clicking settles.
    notifySelectionChanged();
    clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      const container = document.getElementById("graphContainer");
      if (!container || container.classList.contains("is-hidden")) return;
      if (currentGraphMode) renderGraph(currentGraphMode);
    }, 200);
  }
}

/** One shared instance: a genome appearing in two accordions keeps one checkbox state. */
export const viewSelection = new AnalysisViewSelection();

/** The chart tab currently on screen, so a checkbox change knows what to re-render. */
let currentGraphMode = null;

/**
 *
 * @param {string} graphType - Type of graph to render (e.g., "piechart", "timeseries", "heatmap", "bubblemap", "phylotree")
 * @returns {void}
 */
export function renderGraph(graphType, genomeAttributesData = null) {
  // Clear the previous graph
  const graphContainer = document.getElementById("graphContainer");
  if (!graphContainer) {
    console.error("Graph container not found");
    return;
  }

  if (typeof resetGraphContainerStyles === "function") {
    resetGraphContainerStyles("graphContainer");
  }


  // Show the container
  graphContainer.classList.remove("is-hidden");

  // Check if we have genome data
  if ((typeof AppState === 'undefined' || !AppState.state.all_genomes || Object.keys(AppState.state.all_genomes).length === 0) && !genomeAttributesData) {
    if (graphType !== "phylotree") {
      // For all graphs except phylo tree, show no data overlay
      displayNoDataOverlay("graphContainer", "No genome data available");
      return;
    }
  }
  if (graphType && graphType !== "table") currentGraphMode = graphType;

  // Rows unchecked in the tables are left out of every visualization. On pages that
  // never wire up the view selection (result pages) the set is empty and this is the
  // identity.
  // Genomes with no value assigned at position A are left out of every chart, the same
  // rule the tables apply (see buildGenomeRows).
  const data = withAssignedLin(
    viewSelection.filterObject(genomeAttributesData ?? AppState.state.all_genomes ?? {})
  );

  // Choose which graph to render based on the type
  switch (graphType) {
    case "piechart":
      renderPieChart(data);
      break;
    case "timeseries":
      renderTimeSeries(data);
      break;
    case "heatmap":
      renderHeatMap(data);
      break;
    case "bubblemap":
      renderBubbleMap(data);
      break;
    case "phylotree":
      if (!genomeAttributesData) {
        renderPhyloTree("graphContainer");
        break;
      }
      renderPhyloTree("graphContainer", data);
      break;
    default:
      console.error("Unknown graph type:", graphType);
      displayNoDataOverlay("graphContainer", "Unknown graph type");
  }
}

/** 
 * Render Pie Chart for isolation source categories
 * @param {Array} data - The genome data array {genomeID:{}, }.
 * @returns {void}
 */
export function renderPieChart(data) {
  const pieData = processIsolationSourceData(data);

  if (pieData.length === 0) {
    displayNoDataOverlay(
      "graphContainer",
      "No isolation source data available"
    );
    return;
  }

  // Call the pieChart function from the included library
  pieChart("graphContainer", pieData);
}

/** 
 * Process genome data for isolation source categories
 * @param {Object} genomeData  - The genome data object.
 * @returns {Array} - Processed data for pie chart. e.g., [{label: 1, count: 10}, ...]
 */
export function processIsolationSourceData(genomeData) {
  const isolationSourceCounts = {};
  const noDataLabel = -1;

  // Count genomes by isolation source category
  Object.values(genomeData).forEach((genome) => {
    const attributes = attrsOf(genome);
    let source = null;
    if (attributes && attributes.hasOwnProperty("Isolation Source Category")) {
      source = attributes["Isolation Source Category"];
    }
    if (source === null || source === undefined || source === "") {
      source = noDataLabel;
    }
    isolationSourceCounts[source] = (isolationSourceCounts[source] || 0) + 1;
  });

  // Convert to array format expected by the pieChart function
  return Object.entries(isolationSourceCounts).map(([label, count]) => ({
    label: Number.isNaN(Number(label)) ? label : Number(label),
    count: count,
  }));
}

/** 
 * Render Time Series for genome isolation dates
 * @param {Array} data - The genome data array {genomeID:{}, }.
 * @returns {void}
 */
export function renderTimeSeries(data) {
  const { yearData, monthData } = processTimeData(data);

  if (yearData.length === 0 || monthData.length === 0) {
    displayNoDataOverlay("graphContainer", "No time-related data available");
    return;
  }

  // Call the timeSeriesPlot function from the included library
  timeSeriesPlot("graphContainer", yearData, monthData);
}

/** 
 * Process genome data for time series visualizations
 * @param {Object} genomeData - The genome data object.
 * @returns {Object} - Processed data for time series plot. Contains yearData and monthData arrays.
 */
export function processTimeData(genomeData) {
  const d3 = getD3();
  if (!d3) {
    return {
      yearData: [],
      monthData: [],
    };
  }

  const yearCounts = {};
  const monthCounts = {};
  const parseYear = d3.timeParse("%Y");
  const parseMonth = d3.timeParse("%Y-%m");

  // Count genomes by year and month
  Object.values(genomeData).forEach((genome) => {
    const attributes = attrsOf(genome);
    if (attributes && attributes["Date of isolation"]) {
      const dateString = attributes["Date of isolation"];
      const date = new Date(dateString);

      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // JavaScript months are 0-indexed

        const parsedYear = parseYear(year.toString());
        const parsedMonth = parseMonth(`${year}-${month}`);

        if (parsedYear) {
          yearCounts[parsedYear] = (yearCounts[parsedYear] || 0) + 1;
        }

        if (parsedMonth) {
          monthCounts[parsedMonth] = (monthCounts[parsedMonth] || 0) + 1;
        }
      }
    }
  });

  // Convert to array format expected by the timeSeriesPlot function
  const yearData = Object.entries(yearCounts)
    .map(([date, count]) => ({
      Date: new Date(date),
      Count: count,
    }))
    .sort((a, b) => a.Date - b.Date);

  const monthData = Object.entries(monthCounts)
    .map(([date, count]) => ({
      Date: new Date(date),
      Count: count,
    }))
    .sort((a, b) => a.Date - b.Date);

  return {
    yearData,
    monthData,
  };
}

/** 
 * Render Heat Map for country-based data
 * @param {Array} data - The genome data array {genomeID:{}, }.
 * @returns {void}
 */
export async function renderHeatMap(data) {
  const countryData = processCountryData(data);

  if (countryData.length === 0) {
    displayNoDataOverlay("graphContainer", "No country data available");
    return;
  }

  const geoData = await loadGeoDataForGraph("heatmap");
  if (!geoData) return;

  // Call the createHeatMap function from the included library
  createHeatMap("graphContainer", geoData, countryData);
}

/**
 * Fetches the GeoJSON for a map tab, with a loading message while it arrives.
 *
 * @param {string} graphMode the tab that asked, "heatmap" or "bubblemap".
 * @returns {Promise<Object|null>} the GeoJSON, or null when the visitor has moved to
 *   another tab meanwhile (that tab owns the container now) or the fetch failed (an
 *   overlay says so).
 */
async function loadGeoDataForGraph(graphMode) {
  if (!AppState.state.geoData) {
    UIHelper.displayLoadingMessage("graphContainer");
  }
  let geoData;
  try {
    geoData = await ensureGeoData();
  } catch (error) {
    console.error("Error loading geo data:", error);
    if (currentGraphMode === graphMode) {
      displayNoDataOverlay("graphContainer", "Geographic data could not be loaded");
    }
    return null;
  }
  return currentGraphMode === graphMode ? geoData : null;
}

/** 
 * Process genome data for country-based visualizations
 * @param {Object} genomeData - The genome data object.
 * @returns {Array} - Processed data for heat map. e.g., [{"USA": 10}, ...]
 */
export function processCountryData(genomeData) {
  const countryCounts = {};

  // Count genomes by country
  Object.values(genomeData).forEach((genome) => {
    const attributes = attrsOf(genome);
    if (attributes && attributes["Country"]) {
      const country = attributes["Country"];
      countryCounts[country] = (countryCounts[country] || 0) + 1;
    }
  });

  // Convert to array format expected by the createHeatMap function
  return Object.entries(countryCounts).map(([Country, Count]) => ({
    Country,
    Count,
  }));
}

/** 
 * Render Bubble Map for GPS coordinate-based data
 * @param {Array} data - The genome data array {genomeID:{}, }.
 * @returns {void}
 */
export async function renderBubbleMap(data) {
  const locationData = processLocationData(data);

  if (locationData.length === 0) {
    displayNoDataOverlay("graphContainer", "No location data available");
    return;
  }

  const geoData = await loadGeoDataForGraph("bubblemap");
  if (!geoData) return;

  // Call the createBubbleMap function from the included library
  createBubbleMap("graphContainer", geoData, locationData);
}

/** 
 * Process genome data for GPS coordinate-based visualizations
 * @param {Object} genomeData - The genome data object.
 * @returns {Array} - Processed data for bubble map. e.g., [{Lat: xx, Long: yy, Count: zz, Color: category}, ...]
 */
export function processLocationData(genomeData) {
  const locationData = {};

  // Process genomes by GPS coordinates
  Object.values(genomeData).forEach((genome) => {
    const attributes = attrsOf(genome);
    if (attributes) {
      if (attributes["GPS Coordinates"] && attributes["GPS Category"]) {
        const gps = attributes["GPS Coordinates"];
        const category = attributes["GPS Category"];

        if (!locationData[gps]) {
          locationData[gps] = [gps, 0, category];
        }

        locationData[gps][1]++;
      }
    }
  });

  // Convert to array format expected by the createBubbleMap function
  return Object.entries(locationData).map(
    ([gps, [gpsCoords, count, category]]) => {
      const parts = String(gpsCoords).split(" ").map(Number);
      return {
        Lat: parts[0],
        Long: parts[1],
        Count: count,
        Color: +category,
      };
    }
  ).filter(d => isFinite(d.Lat) && isFinite(d.Long) && d.Count > 0);
}

/** 
 * Setup event listeners for storage changes and genome removal
 */
export function setupEventListeners() {
  // Listen for localStorage changes in other tabs
  window.addEventListener("storage", function (event) {
    if (event.key === "Analysis") {
      document.getElementById("storage-change-alert").style.display = "block";
    }
  });

  // Handle genome removal events
  document.addEventListener("genomeRemoved", function (event) {
    const removedGenomeId = event.detail.genomeId;
    const isIdentify = event.detail.isIdentify;
    const removedLinGroupId = event.detail.linGroupId;
    const identType = event.detail.identType; // 'genome' or 'signature'

    AppState.state.phyloJobId = null;
    AppState.state.phylotreeData = null;

    console.log(
      `Removing genome ${removedGenomeId} from AppState and localStorage. Identify: ${isIdentify}, LINgroup ID: ${removedLinGroupId}, Ident Type: ${identType}`
    );
    console.log(
      `Removing genome ${removedLinGroupId} from AppState and localStorage. Identify: ${isIdentify}, LINgroup ID: ${removedLinGroupId}, Ident Type: ${identType}`
    );

    // Update your UI based on the removal
    if (isIdentify) {
      // Handle removal from the new nested identity structure
      if (
        identType &&
        AppState.state.identities[identType] &&
        AppState.state.identities[identType][removedGenomeId]
      ) {
        delete AppState.state.identities[identType][removedGenomeId];
      } else {
        console.debug(
          `[GENOME_REMOVAL] Identity not found in AppState.state.identities structure`,
          {
            identType,
            hasIdentType: !!identType,
            hasIdentitiesForType: !!AppState.state.identities[identType],
            hasSpecificIdentity: !!(
              AppState.state.identities[identType] &&
              AppState.state.identities[identType][removedGenomeId]
            ),
            currentIdentities: AppState.state.identities,
          }
        );
      }
    } else {
      // Check if genome exists in all_genomes before deletion
      if (AppState.state.all_genomes[removedGenomeId]) {
        delete AppState.state.all_genomes[removedGenomeId];
      } else {
      }

      if (removedLinGroupId && AppState.state.linGroups[removedLinGroupId]) {
        // Remove from the specific LINgroup
        const linGroup = AppState.state.linGroups[removedLinGroupId];
        if (linGroup.genomes && Array.isArray(linGroup.genomes)) {
          const index = linGroup.genomes.indexOf(removedGenomeId);
          if (index > -1) {
            linGroup.genomes.splice(index, 1);

            // Add smooth transition with fade effect
            const bodyDiv = document.getElementById(
              `linBody${removedLinGroupId}`
            );
            if (bodyDiv) {
              setTimeout(() => {
                // Update just this specific LINgroup table
                if (!updateSingleLinGroup(removedLinGroupId)) {
                  // Fall back to full re-render if the specific update fails
                  renderLinGroupsTable();
                } else {
                  // Fade in new content
                  bodyDiv.style.opacity = "1";
                  console.debug(`[GENOME_REMOVAL] Fade effect applied`);
                }
              }, 100);
            } else {
              console.debug(
                `[GENOME_REMOVAL] BodyDiv not found, falling back to full table re-render`
              );
              // Fallback if element not found
              renderLinGroupsTable();
            }
          } else {
            console.debug(
              `[GENOME_REMOVAL] Genome ${removedGenomeId} not found in LINgroup genomes array`
            );
          }
        } else {
          console.debug(
            `[GENOME_REMOVAL] LINgroup does not have valid genomes array`,
            {
              hasGenomes: !!linGroup.genomes,
              isArray: Array.isArray(linGroup.genomes),
              genomesValue: linGroup.genomes,
            }
          );
        }
      } else {
        // If it's a regular genome, remove from the main genomes list
        if (AppState.state.genomes && AppState.state.genomes[removedGenomeId]) {
          delete AppState.state.genomes[removedGenomeId];
          // Re-render the genomes table
          renderGenomesTable();
        } else {
          console.debug(
            `[GENOME_REMOVAL] Genome ${removedGenomeId} not found in AppState.state.genomes`,
            {
              hasGenomes: !!AppState.state.genomes,
              hasSpecificGenome: !!(
                AppState.state.genomes && AppState.state.genomes[removedGenomeId]
              ),
              currentGenomes: AppState.state.genomes,
            }
          );
          // Even if genome not found in AppState.state.genomes, refresh the table to ensure UI consistency
          renderGenomesTable();
        }
      }
    }
  });

  document.querySelectorAll(".clear-section-btn").forEach((btn) => {
    btn.addEventListener("click", function (event) {
      event.stopPropagation(); // Prevent the accordion from toggling
      const section = this.getAttribute("data-section");

      const msg =
        section === "lingroups"
          ? "Are you sure you want to clear all LINgroup data?"
          : `Are you sure you want to clear all ${section} data?`;

      if (confirm(msg)) {
        // Get current analysis data
        let analysisData = JSON.parse(localStorage.getItem("Analysis")) || {};
        AppState.state.phyloJobId = null;
        AppState.state.phylotreeData = null;

        // Clear the appropriate section
        switch (section) {
          case "identity":
            // Use correct key and structure for clearing
            analysisData.selectedIdentities = {
              genome: {},
              signature: {},
            };
            AppState.state.identities = {
              genome: {},
              signature: {},
            };
            renderIdentityTable();
            break;
          case "lingroups":
            analysisData.selectedLinGroups = {};
            AppState.state.linGroups = {};
            renderLinGroupsTable();
            AppState.state.all_genomes = AppState.state.genomes;
            break;
          case "genomes":
            // Filter out genomes from all_genomes that are in the genomes listanalysisData.selectedGenomes = {};
            Object.keys(AppState.state.genomes).forEach((genomeId) => {
              if (AppState.state.all_genomes[genomeId]) {
                delete AppState.state.all_genomes[genomeId];
              }
            });
            AppState.state.genomes = {};
            renderGenomesTable();
            break;
        }
        // Save updated data
        localStorage.setItem("Analysis", JSON.stringify(analysisData));
      }
    });
  });

  document.addEventListener("click", function (event) {
    if (event.target.closest(".clear-lingroup-btn")) {
      const button = event.target.closest(".clear-lingroup-btn");
      const groupId = button.getAttribute("data-groupid");
      event.stopPropagation(); // Prevent accordion toggle

      if (confirm(`Are you sure you want to remove this LINgroup?`)) {
        // Get current analysis data
        let analysisData = JSON.parse(localStorage.getItem("Analysis")) || {};

        AppState.state.phyloJobId = null;
        AppState.state.phylotreeData = null;

        // Remove just this specific LINgroup
        if (
          analysisData.selectedLinGroups &&
          analysisData.selectedLinGroups[groupId]
        ) {
          const genomesInDeletedGroup =
            analysisData.selectedLinGroups[groupId].genomes || {};

          // Remove genomes from the main list if they are not in any other LINgroup
          for (const genomeId of genomesInDeletedGroup) {
            delete AppState.state.all_genomes[genomeId];
          }
          delete analysisData.selectedLinGroups[groupId];
          delete AppState.state.linGroups[groupId];

          // Save updated data
          localStorage.setItem("Analysis", JSON.stringify(analysisData));

          // Re-render the LINgroups table
          renderLinGroupsTable();
        } else {
          console.debug(`LINgroup ${groupId} not found in analysis data`);
        }
      } else {
        console.debug("User cancelled LINgroup removal");
      }
    }
  });
}

/**
 * Display an error alert message
 * @param {string} message  - The error message to display.
 */
export function showError(message) {
  // Create or update an error alert
  let errorAlert = document.getElementById("error-alert");

  if (!errorAlert) {
    errorAlert = document.createElement("div");
    errorAlert.id = "error-alert";
    errorAlert.className = "alert alert-danger alert-dismissible fade show";
    errorAlert.style.position = "fixed";
    errorAlert.style.top = "20px";
    errorAlert.style.left = "50%";
    errorAlert.style.transform = "translateX(-50%)";
    errorAlert.style.zIndex = "9999";
    errorAlert.style.minWidth = "300px";

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "btn-close";
    closeButton.setAttribute("data-bs-dismiss", "alert");
    closeButton.setAttribute("aria-label", "Close");

    errorAlert.appendChild(closeButton);
    document.body.appendChild(errorAlert);
  }

  // Update the message
  errorAlert.innerHTML = `<strong>Error:</strong> ${message}`;
  errorAlert.style.display = "block";

  // Hide after 5 seconds
  setTimeout(() => {
    errorAlert.style.display = "none";
  }, 5000);
}

/**
 * Whether the tree may carry the job's own query genomes (the `identity-` leaves).
 *
 * The analysis page is where a submission is placed among its neighbours, so there the
 * query leaves are the point of the tree. A result page lists the genomes the job found,
 * and its tree should hold exactly the rows of the result table, nothing that is not in
 * the database. The result page entry turns this off; every other page keeps the default.
 */
let treeQueryLeaves = true;

export function setTreeQueryLeaves(on) {
  treeQueryLeaves = Boolean(on);
}

export function treeIncludesQueryLeaves() {
  return treeQueryLeaves;
}

/** 
 * Render Phylogenetic Tree
 * @param {string} graphContainerID - The HTML ID of the container to render the tree in.
 * @param {Object} genomeAttributesData - Existing genome attributes.
 * @param {string|null} phyloJobId - Existing phylogenetic job ID, if any.
 * @param {Object} genomes2ident - Identify genomes' attributes.
 * @param {Object} sigs2ident - Identify signatures' attributes.
 */
export function renderPhyloTree(graphContainerID, genomeAttributesData = {}, phyloJobId = null, genomes2ident = {}, sigs2ident = {}) {
  //const baseUrl = `${window.location.protocol}`
  const parentJobID = extractParentJobID();
  const graphContainer = document.getElementById(graphContainerID);
  if (!graphContainer) {
    console.error("Graph container not found");
    return;
  }

  // Clear the previous graph
  // graphContainer.innerHTML = "";

  // check for already computed tree jobID (mainly used in results page)
  if (parentJobID) {
    // A job this browser has not visited is simply absent from the index, so the lookup
    // has to tolerate a miss, it used to throw on an unknown parent job, and on the
    // empty string it handed JSON.parse for a job with no result payload.
    phyloJobId = phyloJobId || getPhylotreeJobId(parentJobID);
  }

  const normalizedData = phylogenyWithDynamicData(genomeAttributesData, genomes2ident, sigs2ident);
  // Unchecked rows stay out of the tree too, both the ID lists a new phylogeny job is
  // built from and the counts below.
  genomeAttributesData = viewSelection.filterObject(normalizedData.all_genomes);
  genomes2ident = viewSelection.filterObject(normalizedData.identities.genomes);
  sigs2ident = viewSelection.filterObject(normalizedData.identities.sigs);
  let identityIDs = normalizedData.identityIDs;
  let baseUrl = normalizedData.baseUrl;
  phyloJobId = phyloJobId || normalizedData.phyloJobId;

  // Correctly count all genomes and identities for the tree
  const genomeCount =
    Object.keys(genomeAttributesData).length +
    Object.keys(genomes2ident).length +
    Object.keys(sigs2ident).length;

  if (genomeCount <= 0) {
    displayNoDataOverlayTree(graphContainer.id, "No genome data available for tree");
    return;
  }
  // Check if we exceed the hard limit
  if (genomeCount > CONFIG.HARD_PHYLOTREE_THRESHOLD) {
    displayNoDataOverlayTree(
      graphContainer.id,
      `Data count is too high (above ${CONFIG.HARD_PHYLOTREE_THRESHOLD}) to generate phylogeny tree`
    );
    return;
  }

  if (typeof AppState !== "undefined" && AppState.state.phylotreeData) {
    // if the data is already present, display it
    displayPhyloTree(
      graphContainerID,
      AppState.state.phylotreeData,
      genomeAttributesData,
      genomes2ident,
      sigs2ident
    );
    return;
  }

  if (
    typeof AppState !== "undefined" &&
    AppState.state.phyloJobId &&
    !phyloJobId
  ) {
    phyloJobId = AppState.state.phyloJobId;
  }

  if (phyloJobId) {
    // Show loading and poll for results
    pollJobStatus(phyloJobId, graphContainer.id)
      .then((result) => {
        // Declared: assigning to an undeclared name is a ReferenceError in a module, and
        // that is what this branch used to do, so a known tree always ended in the
        // catch below and a revisited result never drew its tree.
        const phylotree_job_result = JSON.parse(result);
        if (!phylotree_job_result.success) {
          displayNoDataOverlayTree(
            graphContainer.id,
            "Process was cut short. " + phylotree_job_result.error
          );
          return;
        }

        // Display the tree
        displayPhyloTree(
          graphContainerID,
          phylotree_job_result.data,
          genomeAttributesData,
          genomes2ident,
          sigs2ident
        );
      })
      .catch((error) => {
        console.error("Phylogenetic tree error:", error);
        displayNoDataOverlayTree(
          graphContainer.id,
          "Error generating phylogenetic tree"
        );
      });
    return;
  }

  // Show loading message while starting the job
  const generate_button = displayNoDataOverlayTree(
    graphContainer.id,
    "",
    genomeCount
  );
  GeneratePhylogenyClickable(
    generate_button,
    parentJobID,
    genomeAttributesData,
    genomes2ident,
    sigs2ident
  );
}

/**
 * Extract parent job ID from the current URL
 * @param {string} url - The URL to extract the job ID from. Defaults to current window location.
 * @returns {string|null} - The extracted job ID or null if not found.
 */
export function extractParentJobID(url = `${window.location.href}`) {
  // The same shape the jobs API accepts for a uuid. Job uuids are `<username>-<32 hex>`,
  // and the earlier pattern, 10 to 15 lowercase alphanumerics, matched none of them: no
  // result page ever found its parent, so the tree tab never reused the tree already
  // queued for it and a hand-generated tree was submitted with no parent to link to.
  const match = String(url).match(/\/result\/([A-Za-z0-9._-]{1,128})(?:[/?#]|$)/);
  return match ? match[1] : null;
}

/**
 * The id a query entry is stored under, in whichever shape the entry arrived in.
 *
 * LINflow's Pydantic payload (2.x) names it `id`: the genomeID of a kept upload, the
 * sketchID of a kept sketch, or the QuerySlot id an identify-only run was staged under.
 * Results stored before that carried `_id`. The cart itself is keyed by the same id
 * (`addGenomeToLocal(model._id, model._raw)`), so the key is the last resort for an entry
 * that names neither.
 *
 * @param {object} entry - identity entry from the cart
 * @param {string|undefined} key - the key it is stored under
 * @returns {number|null} the id, or null when the entry carries none
 */
export function identityEntryId(entry, key) {
  const candidates = [entry?.id, entry?._id, key];
  for (const value of candidates) {
    if (value === undefined || value === null || value === "") continue;
    const id = Number(value);
    if (Number.isInteger(id) && id > 0) return id;
  }
  return null;
}

/**
 * Separate identity elements into genomes and signatures for a create_phylogeny job.
 *
 * Regression guard: this used to read only `_filepath` and `_id`, two keys the current
 * payload never writes (see identityEntryId), so every query added from an identify
 * result since the payload models landed was silently left out of the job. The tree then
 * had no query leaf at all, which from the page looked like "the query is not red".
 *
 * A legacy entry whose `_filepath` names a .sig file is still sent by path; everything
 * else goes by id, which is what `genomerxiv-tree -i` resolves through the workspace.
 *
 * @param {Object} genomes2ident - Identify genomes' attributes, keyed by id.
 * @param {Object} sigs2ident - Identify signatures' attributes, keyed by id.
 * @returns {{genomeIds: number[], sigs: string[]}} ids and paths to send with the job.
 */
export function sort_identity_elements(genomes2ident = {}, sigs2ident = {}) {
  const phylotree_identity_data = {
    genomeIds: [],
    sigs: [],
  };
  const seenIds = new Set();
  const seenSigs = new Set();

  for (const bucket of [genomes2ident, sigs2ident]) {
    if (!bucket || typeof bucket !== "object") continue;
    // An array has no meaningful keys, so only a keyed object lends its key as the id.
    const keyed = !Array.isArray(bucket);
    for (const [key, identity] of Object.entries(bucket)) {
      if (!identity || typeof identity !== "object") continue;
      const path = identity._filepath;
      if (typeof path === "string" && path.endsWith(".sig")) {
        if (!seenSigs.has(path)) {
          seenSigs.add(path);
          phylotree_identity_data.sigs.push(path);
        }
        continue;
      }
      const id = identityEntryId(identity, keyed ? key : undefined);
      if (id === null || seenIds.has(id)) continue;
      seenIds.add(id);
      phylotree_identity_data.genomeIds.push(id);
    }
  }
  return phylotree_identity_data;
}

/**
 * Generate Phylogeny on button click
 * @param {HTMLElement} buttonGroup - The button group element to attach the click event to.
 * @param {string|null} parentJobID - Optional parent jobID if this is a sub-job (analysis jobs don't have this, prefix/lingroup member jobs do).
 * @param {Object} genomeAttributesData - Genome attributes data.
 * @param {Object} genomes2ident - Identify genomes' attributes.
 * @param {Object} sigs2ident - Identify signatures' attributes.
 */
export function GeneratePhylogenyClickable(buttonGroup, parentJobID = null, genomeAttributesData = {}, genomes2ident = {}, sigs2ident = {}) {
  if (!buttonGroup) {
    console.error("Button group element not found for phylogeny generation");
    return;
  }
  if (typeof graphContainerID === 'undefined' || !graphContainerID) {
    var graphContainerID = buttonGroup.node().closest("div#graphContainer").id;
  }

  buttonGroup.node().addEventListener("click", async function (e) {
    const phylotree_jobID = await callPhylogenyWithDynamicData(
      parentJobID,
      genomeAttributesData,
      genomes2ident,
      sigs2ident
    );
    document.querySelector(".genome-table").dataset.job_uuid = phylotree_jobID;
    pollJobStatus(phylotree_jobID, graphContainerID).then((result) => {
      // Store the result
      var phylotree_job_result = JSON.parse(result);
      if (!phylotree_job_result.success) {
        displayNoDataOverlayTree(graphContainerID, "Process was cut short. " + phylotree_job_result.error)
      }

      // Display the tree
      displayPhyloTree(graphContainerID, phylotree_job_result.data, genomeAttributesData, genomes2ident, sigs2ident);
    });
  });
}

/** 
 * Prepare phylogeny data with dynamic inputs
 * Can be used in other contexts, e.g. Identify page
 * Throws error if no data is available
 * @param {Object|null} genomeAttributesData - Genome attributes data.
 * @param {Object} genomes2ident - Identify genomes' attributes.
 * @param {Object} sigs2ident - Identify signatures' attributes.
 * @returns {Object} - Normalized data including all_genomes, identities, identityIDs, baseUrl, and phyloJobId.
 */
export function phylogenyWithDynamicData(genomeAttributesData = null, genomes2ident = {}, sigs2ident = {}) {
  const availableAppData = (typeof AppState !== 'undefined');
  const availableGenomeAttributes = (genomeAttributesData && Object.keys(genomeAttributesData).length)
  let baseUrl = "";

  if (!availableGenomeAttributes && !availableAppData) {
    throw new Error('All tree data was empty');
  } else if (!availableGenomeAttributes && availableAppData) {
    genomeAttributesData = AppState.state.all_genomes || {};
    genomes2ident = AppState.state.identities?.genome || {};
    sigs2ident = AppState.state.identities?.signature || {};
    baseUrl = AppState.state.baseUrl || "/api/jobs";
    AppState.state.phyloJobId = null;
  } else if (availableGenomeAttributes) {
    baseUrl = "/api/jobs";
    // Identities live in AppState regardless of where the genome list came from. Only the
    // empty-genomes branch above loaded them, so any caller that supplied genomes --
    // renderGraph("phylotree", data) always does, and a tree needs two genomes anyway --
    // silently dropped the user's uploaded queries and built a tree with no query leaf.
    // Explicitly-passed identities win, so callers that already resolved them are untouched.
    if (
      availableAppData &&
      !Object.keys(genomes2ident).length &&
      !Object.keys(sigs2ident).length
    ) {
      genomes2ident = AppState.state.identities?.genome || {};
      sigs2ident = AppState.state.identities?.signature || {};
    }
  }
  // On a page whose tree excludes the query (a result page, see setTreeQueryLeaves), the
  // identities take no part in anything downstream: not the leaf count, not the labels,
  // and not the job a fresh phylogeny is built from. AppState is shared with the analysis
  // page through localStorage, which is how a query uploaded there turned up in the tree
  // of an unrelated result page.
  if (!treeQueryLeaves) {
    genomes2ident = {};
    sigs2ident = {};
  }
  let phylotree_identity_data = sort_identity_elements(genomes2ident, sigs2ident);
  let phyloJobId = null;

  return {
    "all_genomes": genomeAttributesData,
    "identities": { genomes: genomes2ident, sigs: sigs2ident },
    "identityIDs": phylotree_identity_data,
    "baseUrl": baseUrl,
    "phyloJobId": phyloJobId
  }
}

/**
 * Call phylogeny creation with dynamic data
 * Returns a promise which resolves to the job ID
 * or rejects with an error message
 * If no data is provided, it will try to use AppState data if available
 * If no data is available at all, it will reject
 * If AppState is used, it will update AppState.state.phyloJobId with the new job ID
 * @param {string|null} parentJobID - Optional parent jobID if this is a sub-job (analysis jobs don't have this, prefix/lingroup member jobs do).
 * @param {Object} genomeAttributesData - Genome attributes data.
 * @param {Object} genomes2ident - Identify genomes' attributes.
 * @param {Object} sigs2ident - Identify signatures' attributes.
 * @param {string} containerID - The HTML ID of the container to render the tree in.
 * @returns {Promise<string>} - Promise resolving to the phylogeny job ID.
 * usage:
 * CallPhylogenyWithDynamicData("", genomeAttributesData, genomes2ident, sigs2ident)
 *   .then(jobId => console.log("Phylogeny job created with ID:", jobId))
 *   .catch(error => console.error("Error creating phylogeny:", error));
 */
export async function callPhylogenyWithDynamicData(parentJobID = null, genomeAttributesData = {}, genomes2ident = {}, sigs2ident = {}, containerID = "graphContainer") {
  const normalizedData = phylogenyWithDynamicData(genomeAttributesData, genomes2ident, sigs2ident);
  // Unchecked rows stay out of the tree too, both the ID lists a new phylogeny job is
  // built from and the counts below.
  genomeAttributesData = viewSelection.filterObject(normalizedData.all_genomes);
  genomes2ident = viewSelection.filterObject(normalizedData.identities.genomes);
  sigs2ident = viewSelection.filterObject(normalizedData.identities.sigs);
  let identityIDs = normalizedData.identityIDs;
  let baseUrl = normalizedData.baseUrl;
  parentJobID = parentJobID || extractParentJobID() || "";
  const availableAppData = (typeof AppState !== 'undefined');

  try {
    const jobId = await handlePhylogenyCreation(
      Object.keys(genomeAttributesData),
      normalizedData.identityIDs,
      parentJobID,
    );
    if (availableAppData) {
      AppState.state.phyloJobId = jobId;
      // AppState and localStorage use the singular keys. normalizedData keeps its
      // historical {genomes, sigs} shape for this function's callers, so translate
      // explicitly instead of leaving subsequent renders unable to find identities.
      // Not on a page that excludes the query: its empty identities would wipe the
      // analysis page's stored uploads.
      if (treeQueryLeaves) {
        AppState.state.identities = {
          genome: genomes2ident,
          signature: sigs2ident,
        };
      }
    }
    return jobId;
  } catch (error) {
    if (TREE_LOGGING)
      console.error("Error creating phylogeny:", error);
    displayNoDataOverlayTree(
      containerID,
      "Error creating phylogeny tree: " + error.message
    );
  }
}

/** 
 * Function to display the prompt for generating the phylotree
 * @param {string} graphContainerID - The HTML ID of the container to render the prompt in.
 * @param {number} genomeCount - The number of genomes available for tree generation.
 * @returns {HTMLElement} - The button which needs to hook into a click with phylogenetic tree generation.
 */
export function displayPhyloTreeGenerationPrompt(graphContainerID, genomeCount) {
  const container = document.getElementById(graphContainerID);
  if (!container) {
    document.getElementById("alert-text").textContent = `Element ${graphContainerID} not found to generate graph`;
    return;
  }
  // const globalData = typeof AppState !== 'undefined' ? AppState : {};
  // genomeCount = typeof genomeAttributesData !== 'undefined' ? Object.keys(genomeAttributesData).length : 0;


  // Clear the container
  container.innerHTML = "";

  // Create SVG element
  const svg = document.createElementNS(CONFIG.SVG_NAMESPACE, "svg");
  svg.setAttribute("id", "phylo_tree_prompt");
  const chartSize =
    typeof getChartContainerDimensions === "function"
      ? getChartContainerDimensions(container, 960, 420)
      : {
        width: container.clientWidth || 960,
        height: container.clientHeight || 420,
      };
  const width = chartSize.width;
  const height = chartSize.height;

  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  svg.setAttribute("width", `${width}`);
  svg.setAttribute("height", `${height}`);
  svg.style.width = "100%";
  svg.style.height = `${height}px`;
  svg.style.border = "0";

  // Add background rectangle
  const rect = document.createElementNS(CONFIG.SVG_NAMESPACE, "rect");
  rect.setAttribute("x", "0");
  rect.setAttribute("y", "0");
  rect.setAttribute("width", "100%");
  rect.setAttribute("height", "100%");
  rect.style.fill = "var(--color-surface-muted, #f8f8f8)";
  rect.style.opacity = "0.8";
  svg.appendChild(rect);

  // Add message group
  const messageGroup = document.createElementNS(
    CONFIG.SVG_NAMESPACE,
    "g"
  );
  messageGroup.setAttribute("class", "threshold-message-group");
  messageGroup.setAttribute(
    "transform",
    `translate(${width / 2}, ${height / 2 - 50})`
  );

  // Add warning icon
  const circle = document.createElementNS(
    CONFIG.SVG_NAMESPACE,
    "circle"
  );
  circle.setAttribute("r", "30");
  circle.setAttribute("cx", "0");
  circle.setAttribute("cy", "-50");
  circle.style.fill = "var(--color-warn-wash, #fff9e6)";
  circle.style.stroke = "#4793c7";
  circle.style.strokeWidth = "2";
  messageGroup.appendChild(circle);

  // Add icon text
  const iconText = document.createElementNS(
    CONFIG.SVG_NAMESPACE,
    "text"
  );
  iconText.setAttribute("x", "0");
  iconText.setAttribute("y", "-50");
  iconText.setAttribute("text-anchor", "middle");
  iconText.setAttribute("dy", ".35em");
  iconText.style.fontSize = "24px";
  iconText.style.fontWeight = "bold";
  iconText.style.fill = "#4793c7";
  iconText.textContent = "i";
  messageGroup.appendChild(iconText);

  // Add message text
  const messageText = document.createElementNS(
    CONFIG.SVG_NAMESPACE,
    "text"
  );
  messageText.setAttribute("class", "threshold-message");
  messageText.setAttribute("x", "0");
  messageText.setAttribute("y", "0");
  messageText.setAttribute("text-anchor", "middle");
  messageText.setAttribute("dy", ".35em");
  messageText.style.fontSize = "18px";
  messageText.style.fill = "var(--color-text, #666666)";
  messageText.textContent = `Data count is ${genomeCount}, do you want to generate a phylogenetic tree?`;
  messageGroup.appendChild(messageText);

  // Add subtitle text
  const subtitleText = document.createElementNS(
    CONFIG.SVG_NAMESPACE,
    "text"
  );
  subtitleText.setAttribute("class", "threshold-message-subtitle");
  subtitleText.setAttribute("x", "0");
  subtitleText.setAttribute("y", "30");
  subtitleText.setAttribute("text-anchor", "middle");
  subtitleText.setAttribute("dy", ".35em");
  subtitleText.style.fontSize = "14px";
  subtitleText.style.fill = "var(--color-text-muted, #888888)";
  subtitleText.textContent = "(it may take a couple of minutes)";
  messageGroup.appendChild(subtitleText);

  svg.appendChild(messageGroup);

  // Add generate button
  const buttonGroup = document.createElementNS(
    CONFIG.SVG_NAMESPACE,
    "g"
  );
  buttonGroup.setAttribute("class", "generate-button");
  buttonGroup.setAttribute(
    "transform",
    `translate(${width / 2}, ${height / 2 + 50})`
  );
  buttonGroup.style.cursor = "pointer";

  // Add button rectangle
  const buttonRect = document.createElementNS(
    CONFIG.SVG_NAMESPACE,
    "rect"
  );
  buttonRect.setAttribute("x", "-75");
  buttonRect.setAttribute("y", "-20");
  buttonRect.setAttribute("width", "150");
  buttonRect.setAttribute("height", "40");
  buttonRect.setAttribute("rx", "5");
  buttonRect.setAttribute("ry", "5");
  buttonRect.style.fill = "#4793c7";
  buttonRect.style.stroke = "#3d84ad";
  buttonRect.style.strokeWidth = "2";
  buttonGroup.appendChild(buttonRect);

  // Add button text
  const buttonText = document.createElementNS(
    CONFIG.SVG_NAMESPACE,
    "text"
  );
  buttonText.setAttribute("x", "0");
  buttonText.setAttribute("y", "5");
  buttonText.setAttribute("text-anchor", "middle");
  buttonText.style.fontSize = "16px";
  buttonText.style.fontWeight = "bold";
  buttonText.style.fill = "white";
  buttonText.textContent = "Generate";
  buttonGroup.appendChild(buttonText);

  svg.appendChild(buttonGroup);
  container.appendChild(svg);

  return buttonGroup;
}

/** 
 * Display the phylogenetic tree using the provided data
 * @param {string} graph_containerID - The HTML ID of the container to render the tree in.
 * @param {Object} phyloResult - The phylogenetic tree data.
 * @param {Object} genomeAttributesData - Genome attributes data.
 * @param {Object} genomes2ident - Identify genomes' attributes.
 * @param {Object} sigs2ident - Identify signatures' attributes.
 */
export function displayPhyloTree(graph_containerID, phyloResult, genomeAttributesData = {}, genomes2ident = {}, sigs2ident = {}) {

  // The cache keeps the FULL tree: pruning below is per current checkboxes, and a
  // re-checked row has to be able to come back without re-running the job.
  if (typeof AppState !== 'undefined') {
    AppState.state.phylotreeData = phyloResult;
  }

  // Unchecked rows are pruned out of the displayed tree, by RAW leaf name (genomeID or
  // identity-<key>), before any renaming. What remains is the induced subtree of the
  // checked set; hitting Generate again computes a true phylogeny of the subset.
  let treeSource = phyloResult;
  if (viewSelection.excluded.size) {
    const pruned = pruneLeaves(phyloResult, (name) => viewSelection.isExcludedRawLeaf(name));
    if (!pruned) {
      displayNoDataOverlayTree(graph_containerID, "Every row is unchecked; nothing to draw");
      return null;
    }
    treeSource = pruned;
  }
  // A phylogeny job started from an identify run carries the query as `identity-<key>`
  // leaves. On a result page those are pruned here, at display time, so the cached tree
  // stays complete and the analysis page can still draw the same job with the query in.
  if (!treeQueryLeaves) {
    const pruned = pruneLeaves(treeSource, (name) => String(name).startsWith(IDENTITY_PREFIX));
    if (!pruned) {
      displayNoDataOverlayTree(graphContainerID, "The tree holds only the query; nothing to draw");
      return null;
    }
    treeSource = pruned;
  }

  // Create a temporary merged map of all known genomes/identities for name replacement
  const allKnownItemsForTree = {
    ...genomeAttributesData,
    ...genomes2ident,
    ...sigs2ident,
  };

  const treeData = replaceLeafNames(treeSource, allKnownItemsForTree);
  const tree_mapping = structureTreeLeafMapping(allKnownItemsForTree);

  if (typeof AppState !== 'undefined') {
    //AppState.state.treeData = tree_mapping;
    AppState.state.treeData = treeData;
  }

  // treeData is [plainNewick, metadataNewick, queryLabels]. The metadata twin was never
  // handed over before, so the toolbar's metadata switch had nothing to switch to. The
  // labels are a secondary signal for the red query leaves; the "Query: " marker in the
  // leaf name is the primary one (see isQueryName in tree.js).
  let tree = create_tree(
    graph_containerID,
    treeData[0],
    treeData[1],
    false,
    treeData[2]
  );
  // Apply the mapping for better labels
  //tree = applyTreeLeafMapping(tree, tree_mapping, 2);
  // Setup resize observer for responsive layout
  //setupResizeObserver(tree);
  return tree;
}

/** 
 * Add a count badge to a header element
 * @param {HTMLElement} headerEl - The header element to add the badge to.
 * @param {number} count - The count to display in the badge.
 */
export function addCountBadge(headerEl, count) {
  // remove the old badge (if any)
  const old = headerEl.querySelector(".count-badge");
  if (old) old.remove();

  // add fresh badge
  const badge = document.createElement("span");
  badge.className = "ms-2 badge rounded-pill count-badge bg-primary";
  //badge.style.background = '#d1d1d1'; // light-grey
  badge.textContent = count;
  headerEl.appendChild(badge);
}

/** 
 * Display a "No Data Available" overlay with a prompt to generate a phylogeny tree
 * @param {string} containerID - The HTML ID of the container to render the overlay in.
 * @param {string} message - The message to display in the overlay.
 * @param {number} genome_count - The number of genomes available for tree generation.
 * @returns {HTMLElement} - The button which needs to hook into a click with phylogenetic tree generation.
 */
export function displayNoDataOverlayTree(containerID, message = "No Data Available", genome_count = 0) {
  const d3 = getD3();
  if (!d3) {
    console.error("D3 is not available for tree overlay rendering");
    return null;
  }

  const graphContainer = displayNoDataOverlay(containerID, message).node();
  const svg = d3.select(graphContainer.querySelector("svg"));
  const messageGroup = d3.select(graphContainer.querySelector("g"));

  const chartSize =
    typeof getChartContainerDimensions === "function"
      ? getChartContainerDimensions(containerID, 960, 420)
      : {
        width: graphContainer.clientWidth || 960,
        height: graphContainer.clientHeight || 420,
      };
  const width = chartSize.width;
  const height = chartSize.height;

  svg
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("width", width)
    .attr("height", height)
    .style("height", `${height}px`);

  messageGroup.attr("transform", `translate(${width / 2}, ${height / 2})`);

  if (genome_count < 2)
    return;
  messageGroup.node().querySelector("text").textContent = "";

  // Add the "No Data Available" text
  messageGroup.append("text")
    .attr("class", "no-data-message")
    .attr("x", 0)
    .attr("y", 0)
    .attr("text-anchor", "middle")
    .attr("dy", ".35em")
    .style("font-size", "20px")
    .style("font-weight", "bold")
    .style("fill", "var(--color-text, #666666)")
    .text(
      `Genome data count is ${genome_count}, 
                    do you want to generate a phylogeny tree?`
    );

  messageGroup
    .append("text")
    .attr("class", "threshold-message-subtitle")
    .attr("x", 0)
    .attr("y", 30)
    .attr("text-anchor", "middle")
    .attr("dy", ".35em")
    .style("font-size", "14px")
    .style("fill", "var(--color-text-muted, #888888)")
    .text("(it may take couple of minutes.)");
  // Add generate button
  const buttonGroup = svg
    .append("g")
    .attr("class", "generate-button")
    .attr("transform", `translate(${width / 2}, ${height / 2 + 100})`)
    .style("cursor", "pointer")
    /*.on("click", async function() {
        try {
            // Call the async function that returns a promise
            const jobUuidPromise = handlePhylogenyCreation(
                Object.keys(data), {},
                job_url
            );

            // Store the promise in the global variable
            await jobUuidPromise;

            phylotree_job_uuid = jobUuidPromise;

            // Call the callback function to load the phylogeny tree
            callbackFunction();
        } catch (error) {
            console.error("Error creating phylogeny:", error);
            displayNoDataOverlayTree(
                containerID,
                "Error creating phylogeny tree: " + error.message
            );
        }
    })*/
    ;

  buttonGroup
    .append("rect")
    .attr("x", -75)
    .attr("y", -20)
    .attr("width", "150")
    .attr("height", 40)
    .attr("rx", 5)
    .attr("ry", 5)
    .style("fill", "#55caff")
    .style("stroke", "none")
    .style("stroke", "#88c8c")
    .style("stroke-width", 2);

  buttonGroup
    .append("text")
    .attr("x", 0)
    .attr("y", 5)
    .attr("text-anchor", "middle")
    .style("font-size", "16px")
    .style("font-weight", "bold")
    .style("fill", "white")
    .text("Generate");

  return buttonGroup;
}


export function toggleAnalysisGraphVisibility(showGraph) {
  var graphContainer = document.getElementById('graphContainer');
  if (!graphContainer) {
    return;
  }
  graphContainer.classList.toggle('is-hidden', !showGraph);
}

export function initAnalysisTabs() {
  var tabList = document.getElementById('genome-search-tabs');
  if (!tabList) {
    return;
  }

  tabList.addEventListener('click', function (event) {
    var tabButton = event.target.closest('button[data-graph-mode]');
    if (!tabButton) {
      return;
    }

    var graphMode = tabButton.getAttribute('data-graph-mode');
    if (graphMode === 'table') {
      toggleAnalysisGraphVisibility(false);
      return;
    }

    renderGraph(graphMode);
    toggleAnalysisGraphVisibility(true);
  });
}
